import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api/axiosInstance';
import { toast } from 'sonner';

const t = (name, fn) =>
  createAsyncThunk(name, async (arg, { rejectWithValue }) => {
    try {
      return await fn(arg);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Request failed');
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  });

// ─── Existing ────────────────────────────────────────────────────────────────

export const scanDispatch = t('dispatch/scan', async ({ qrCode, location, sapInvoiceNo }) => {
  const params = new URLSearchParams({ qrCode });
  if (location) params.append('location', location);
  if (sapInvoiceNo) params.append('sapInvoiceNo', sapInvoiceNo);
  const { data } = await api.post(`Dispatch/scan?${params.toString()}`);
  toast.success('Product dispatched — warranty started');
  return data.data;
});

export const fetchDispatches = t('dispatch/fetchAll', async (params = {}) => {
  const { data } = await api.get('Dispatch', { params });
  return data.data;
});

// ─── New: Bulk Batch Dispatch ─────────────────────────────────────────────────

export const fetchAvailableBatches = t('dispatch/fetchAvailableBatches', async () => {
  const { data } = await api.get('Dispatch/available-batches');
  return data.data;
});

export const bulkBatchDispatch = t('dispatch/bulkBatch', async (dto) => {
  const { data } = await api.post('Dispatch/bulk-batch', dto);
  toast.success(`Bulk dispatch complete — ${data.data.totalDispatched.toLocaleString()} items dispatched`);
  return data.data;
});

// ─── Slice ────────────────────────────────────────────────────────────────────

const dispatchSlice = createSlice({
  name: 'dispatch',
  initialState: {
    items: [],
    totalCount: 0,
    lastScan: null,
    loading: false,
    scanLoading: false,
    error: null,
    availableBatches: [],
    availableLoading: false,
    bulkLoading: false,
    bulkResult: null
  },
  reducers: {
    clearLastScan: (s) => {
      s.lastScan = null;
    },
    clearBulkResult: (s) => {
      s.bulkResult = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // scan
      .addCase(scanDispatch.pending, (s) => {
        s.scanLoading = true;
        s.error = null;
      })
      .addCase(scanDispatch.fulfilled, (s, a) => {
        s.scanLoading = false;
        s.lastScan = a.payload;
      })
      .addCase(scanDispatch.rejected, (s, a) => {
        s.scanLoading = false;
        s.error = a.payload;
      })

      // fetch dispatches
      .addCase(fetchDispatches.pending, (s) => {
        s.loading = true;
      })
      .addCase(fetchDispatches.fulfilled, (s, a) => {
        s.loading = false;
        s.items = a.payload?.items || [];
        s.totalCount = a.payload?.totalCount || 0;
      })
      .addCase(fetchDispatches.rejected, (s) => {
        s.loading = false;
      })

      // available batches
      .addCase(fetchAvailableBatches.pending, (s) => {
        s.availableLoading = true;
      })
      .addCase(fetchAvailableBatches.fulfilled, (s, a) => {
        s.availableLoading = false;
        s.availableBatches = a.payload || [];
      })
      .addCase(fetchAvailableBatches.rejected, (s) => {
        s.availableLoading = false;
      })

      // bulk dispatch
      .addCase(bulkBatchDispatch.pending, (s) => {
        s.bulkLoading = true;
        s.bulkResult = null;
      })
      .addCase(bulkBatchDispatch.fulfilled, (s, a) => {
        s.bulkLoading = false;
        s.bulkResult = a.payload;
      })
      .addCase(bulkBatchDispatch.rejected, (s) => {
        s.bulkLoading = false;
      });
  }
});

export const { clearLastScan, clearBulkResult } = dispatchSlice.actions;
export default dispatchSlice.reducer;
